import { Link } from 'react-router';
import { ArrowUpRight } from 'lucide-react';
import { Icon } from '../../../ds/components';
import type { TicketDetail } from '../types';
import { DeadlineDot } from '../ui/DeadlineDot';
import { TicketAvatar } from '../ui/TicketAvatar';
import { initialsOf } from '../ui/initials';
import { FieldMenu } from './FieldMenu';
import styles from './detail.module.css';

export interface SidebarProps {
  detail: TicketDetail;
  pending: boolean;
  onAssign: (assigneeId: string) => void;
  onCategory: (categoryId: string) => void;
}

/**
 * The right column of Backoffice 05–08: „Zuordnung" (Zuständig and Kategorie change the ticket, Bezug links to the
 * deal or unit), „Frist" (dot + value + note) and „Kontakt" (name → contact record, Weitere Tickets, Portal).
 */
export function Sidebar({ detail, pending, onAssign, onCategory }: SidebarProps) {
  const { sidebar, options } = detail;
  const closed = detail.status === 'closed';
  return (
    <div className={styles.sidebar}>
      <section className={styles.sideCard} aria-labelledby={`${detail.id}-assignment`}>
        <h2 id={`${detail.id}-assignment`} className={styles.eyebrow}>
          Zuordnung
        </h2>
        <div className={styles.fields}>
          <FieldMenu
            label="Zuständig"
            placeholder="Nicht zugeordnet"
            value={sidebar.assignee?.id ?? null}
            disabled={pending || closed || !detail.actions.assign}
            options={options.assignees.map((person) => ({
              id: person.id,
              label: person.name,
              leading: <TicketAvatar initials={person.initials} size={20} tone="dark" />,
            }))}
            display={
              sidebar.assignee ? (
                <>
                  <TicketAvatar initials={sidebar.assignee.initials} size={20} tone="dark" />
                  {sidebar.assignee.name}
                </>
              ) : undefined
            }
            onChange={onAssign}
          />
          <FieldMenu
            label="Kategorie"
            placeholder="Ohne Kategorie"
            value={sidebar.category?.id ?? null}
            disabled={pending || closed}
            options={options.categories.map((category) => ({ id: category.id, label: category.name }))}
            onChange={onCategory}
          />
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Bezug</span>
            {sidebar.reference ? (
              <Link to={sidebar.reference.href} className={styles.fieldControl}>
                <span className={styles.fieldValue}>{sidebar.reference.label}</span>
                <Icon icon={ArrowUpRight} size={16} className={styles.fieldCaret} />
              </Link>
            ) : (
              <span className={styles.fieldStatic}>Kein Bezug</span>
            )}
          </div>
        </div>
      </section>

      <section className={styles.sideCard} aria-labelledby={`${detail.id}-deadline`}>
        <h2 id={`${detail.id}-deadline`} className={styles.eyebrow}>
          Frist
        </h2>
        <div className={styles.deadlineValue}>
          <DeadlineDot tone={sidebar.deadline.tone} size={8} />
          <span>{sidebar.deadline.value}</span>
        </div>
        {sidebar.deadline.note ? <div className={styles.deadlineNote}>{sidebar.deadline.note}</div> : null}
      </section>

      {sidebar.contact ? (
        <section className={styles.sideCard} aria-labelledby={`${detail.id}-contact`}>
          <h2 id={`${detail.id}-contact`} className={styles.eyebrow}>
            Kontakt
          </h2>
          <div className={styles.contactHead}>
            <TicketAvatar initials={initialsOf(sidebar.contact.name)} size={32} tone="light" />
            <div className={styles.contactText}>
              <Link to={`/contacts/${sidebar.contact.id}`} className={styles.contactName}>
                {sidebar.contact.name}
              </Link>
              <div className={styles.contactLine}>{sidebar.contact.line}</div>
            </div>
          </div>
          <dl className={styles.facts}>
            <div className={styles.fact}>
              <dt>Weitere Tickets</dt>
              <dd>{sidebar.contact.otherTickets}</dd>
            </div>
            <div className={styles.fact}>
              <dt>Portal</dt>
              <dd>{sidebar.contact.portal}</dd>
            </div>
          </dl>
        </section>
      ) : null}
    </div>
  );
}
